/**
 * AME Power Wheelchair Configurator — guided quiz for power wheelchair selection
 * ES5 IIFE, no dependencies, deferred loading
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-ame-pwc-configurator]');
  if (!root) return;

  // --- DOM refs ---
  var ctaBtn = root.querySelector('[data-pwc-cfg-cta]');
  var wizard = root.querySelector('#ame-pwc-cfg-wizard');
  var resultsPanel = root.querySelector('[data-pwc-cfg-results]');
  var resultsGrid = root.querySelector('[data-pwc-cfg-results-grid]');
  var resultsHeading = root.querySelector('[data-pwc-cfg-results-heading]');
  var resultsSubtitle = root.querySelector('[data-pwc-cfg-results-subtitle]');
  var viewAllLink = root.querySelector('[data-pwc-cfg-view-all]');
  var progressLabel = root.querySelector('[data-pwc-cfg-progress-label]');
  var progressBar = root.querySelector('[data-pwc-cfg-progressbar]');
  var progressFill = root.querySelector('[data-pwc-cfg-progress-fill]');
  var liveRegion = root.querySelector('[data-pwc-cfg-live]');

  var steps = root.querySelectorAll('[data-pwc-cfg-step]');
  var TOTAL_STEPS = steps.length;

  // --- State ---
  var currentStep = 1;
  var answers = {};
  var products = [];

  // --- Parse product data ---
  var dataEl = document.getElementById('ame-pwc-cfg-data');
  if (dataEl) {
    try {
      products = JSON.parse(dataEl.textContent);
    } catch (e) {
      products = [];
    }
  }

  // --- Session storage helpers ---
  function saveState() {
    try {
      sessionStorage.setItem('ame-pwc-cfg-state', JSON.stringify({
        step: currentStep,
        answers: answers
      }));
    } catch (e) { /* ignore */ }
  }

  function loadState() {
    try {
      var raw = sessionStorage.getItem('ame-pwc-cfg-state');
      if (raw) return JSON.parse(raw);
    } catch (e) { /* ignore */ }
    return null;
  }

  function clearState() {
    try { sessionStorage.removeItem('ame-pwc-cfg-state'); } catch (e) { /* ignore */ }
  }

  // --- Announce to screen readers ---
  function announce(msg) {
    if (liveRegion) liveRegion.textContent = msg;
  }

  // --- Progress bar ---
  function updateProgress(step) {
    var pct = Math.round((step / TOTAL_STEPS) * 100);
    if (progressLabel) progressLabel.textContent = 'Step ' + step + ' of ' + TOTAL_STEPS;
    if (progressFill) progressFill.style.width = pct + '%';
    if (progressBar) {
      progressBar.setAttribute('aria-valuenow', pct);
    }
  }

  // --- Show/hide steps ---
  function showStep(n) {
    for (var i = 0; i < steps.length; i++) {
      steps[i].classList.toggle('is-active', i === n - 1);
    }
    currentStep = n;
    updateProgress(n);

    var stepEl = steps[n - 1];
    var radios = stepEl.querySelectorAll('input[type="radio"]');
    var fieldName = radios.length > 0 ? radios[0].name : '';
    if (fieldName && answers[fieldName]) {
      for (var j = 0; j < radios.length; j++) {
        radios[j].checked = radios[j].value === answers[fieldName];
      }
    }

    updateNextBtn(stepEl);

    var question = stepEl.querySelector('.ame-pwc-cfg__question');
    if (question) question.focus();

    announce('Step ' + n + ' of ' + TOTAL_STEPS);
    saveState();
  }

  // --- Next button enabled/disabled ---
  function updateNextBtn(stepEl) {
    var nextBtn = stepEl.querySelector('[data-pwc-cfg-next]');
    var checked = stepEl.querySelector('input[type="radio"]:checked');
    if (nextBtn) nextBtn.disabled = !checked;
  }

  // --- Open wizard ---
  function openWizard() {
    wizard.hidden = false;
    ctaBtn.setAttribute('aria-expanded', 'true');
    showStep(currentStep);
    requestAnimationFrame(function () {
      var rect = wizard.getBoundingClientRect();
      var headerEl = document.querySelector('#shopify-section-header') || document.querySelector('.section-header');
      var headerH = headerEl ? headerEl.getBoundingClientRect().height : 0;
      window.scrollTo({ top: window.scrollY + rect.top - headerH - 16, behavior: 'smooth' });
    });
  }

  // --- Close wizard ---
  function closeWizard() {
    wizard.hidden = true;
    resultsPanel.classList.remove('is-active');
    ctaBtn.setAttribute('aria-expanded', 'false');
    ctaBtn.focus();
    currentStep = 1;
    answers = {};
    clearState();
  }

  // --- Collect answer from current step ---
  function collectAnswer() {
    var stepEl = steps[currentStep - 1];
    var checked = stepEl.querySelector('input[type="radio"]:checked');
    if (checked) {
      answers[checked.name] = checked.value;
    }
  }

  // --- Tag helper ---
  function hasTag(tags, tag) {
    if (!tags || !tags.length) return false;
    for (var i = 0; i < tags.length; i++) {
      if (tags[i] === tag) return true;
    }
    return false;
  }

  // --- Scoring algorithm ---
  function scoreUseCase(product, useCase) {
    var tags = product.tags || [];
    var score = 0;
    switch (useCase) {
      case 'daily':
        if (hasTag(tags, 'power-wheelchair')) score += 8;
        if (product.range_miles !== null && product.range_miles > 10) score += 4;
        if (product.max_speed !== null && product.max_speed >= 4) score += 3;
        if (product.seat_width !== null) score += 2;
        break;
      case 'travel':
        if (hasTag(tags, 'folding')) score += 8;
        if (hasTag(tags, 'travel')) score += 6;
        if (hasTag(tags, 'portable')) score += 5;
        if (product.disassembles) score += 5;
        if (product.product_weight !== null && product.product_weight < 60) score += 5;
        if (!hasTag(tags, 'folding') && !hasTag(tags, 'travel') && !hasTag(tags, 'portable')) score -= 5;
        break;
      case 'heavy_duty':
        if (hasTag(tags, 'heavy-duty')) score += 10;
        if (hasTag(tags, 'bariatric')) score += 6;
        if (product.weight_capacity !== null && product.weight_capacity >= 400) score += 5;
        if (product.range_miles !== null && product.range_miles > 15) score += 4;
        if (!hasTag(tags, 'heavy-duty') && !hasTag(tags, 'bariatric')) score -= 3;
        break;
      case 'compact':
        if (hasTag(tags, 'compact')) score += 8;
        if (product.product_weight !== null && product.product_weight < 80) score += 4;
        if (hasTag(tags, 'folding')) score += 3;
        if (hasTag(tags, 'heavy-duty') || hasTag(tags, 'bariatric')) score -= 5;
        break;
    }
    return score;
  }

  function scoreCapacity(product, minCapacity) {
    if (product.weight_capacity === null) return 0;
    if (product.weight_capacity < minCapacity) return -100;
    return 10;
  }

  function scorePortability(product, portability) {
    var tags = product.tags || [];
    var score = 0;
    switch (portability) {
      case 'must_fold':
        if (hasTag(tags, 'folding')) score += 8;
        if (product.disassembles) score += 5;
        if (hasTag(tags, 'portable')) score += 4;
        if (hasTag(tags, 'travel')) score += 3;
        if (product.product_weight !== null && product.product_weight < 60) score += 5;
        if (!hasTag(tags, 'folding') && !product.disassembles) score -= 8;
        break;
      case 'nice_to_have':
        if (hasTag(tags, 'folding')) score += 3;
        if (product.disassembles) score += 3;
        if (hasTag(tags, 'compact')) score += 2;
        break;
      case 'not_important':
        if (hasTag(tags, 'full-size')) score += 4;
        if (hasTag(tags, 'heavy-duty')) score += 3;
        if (product.range_miles !== null && product.range_miles > 15) score += 3;
        break;
    }
    return score;
  }

  function scoreBudget(product, budget) {
    if (product.call_for_price) return 5;
    var priceDollars = product.price / 100;
    var lo = 0;
    var hi = 999999;

    switch (budget) {
      case '2000':
        lo = 0; hi = 2000;
        break;
      case '4000':
        lo = 2000; hi = 4000;
        break;
      case '6000':
        lo = 4000; hi = 6000;
        break;
      case '9999':
        lo = 6000; hi = 999999;
        break;
    }

    if (priceDollars >= lo && priceDollars <= hi) return 10;
    if (priceDollars <= hi * 1.2) return 5;
    return 0;
  }

  function scoreProducts(ans) {
    var useCase = ans.pwc_use_case || 'daily';
    var minCapacity = parseInt(ans.pwc_weight, 10) || 250;
    var portability = ans.pwc_portability || 'nice_to_have';
    var budget = ans.pwc_budget || '9999';

    var scored = [];
    for (var i = 0; i < products.length; i++) {
      var p = products[i];

      var uScore = scoreUseCase(p, useCase);
      var cScore = scoreCapacity(p, minCapacity);
      var pScore = scorePortability(p, portability);
      var bScore = scoreBudget(p, budget);

      // Weights: use_case=3, weight=4 (hard), portability=3, budget=1
      var total = (uScore * 3) + (cScore * 4) + (pScore * 3) + (bScore * 1);

      scored.push({
        product: p,
        total: total,
        useScore: uScore,
        capacityScore: cScore,
        portabilityScore: pScore,
        budgetScore: bScore
      });
    }

    scored.sort(function (a, b) { return b.total - a.total; });
    return scored;
  }

  // --- Match badge label ---
  function getMatchLabel(index) {
    switch (index) {
      case 0: return 'Best Match';
      case 1: return 'Great Fit';
      case 2: return 'Strong Pick';
      default: return 'Good Option';
    }
  }

  // --- Format price ---
  function formatPrice(cents) {
    var dollars = (cents / 100).toFixed(2);
    return '$' + dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // --- Build spec pills ---
  function buildSpecs(product) {
    var specs = [];
    if (product.weight_capacity) specs.push(product.weight_capacity + ' lb capacity');
    if (product.range_miles) specs.push(product.range_miles + ' mi range');
    if (product.max_speed) specs.push(product.max_speed + ' mph');
    if (product.product_weight) specs.push(product.product_weight + ' lbs');
    if (product.disassembles) specs.push('Folds/Disassembles');
    return specs.slice(0, 3);
  }

  // --- HTML escaping ---
  function escapeHtml(str) {
    if (!str) return '';
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  // --- Render result cards ---
  function renderResults(scored) {
    var top = [];
    for (var i = 0; i < scored.length && top.length < 5; i++) {
      if (scored[i].total > 0) top.push(scored[i]);
    }

    if (top.length === 0 && scored.length > 0) {
      for (var f = 0; f < scored.length && top.length < 3; f++) {
        top.push(scored[f]);
      }
    }

    resultsGrid.innerHTML = '';

    if (top.length === 0) {
      resultsGrid.innerHTML =
        '<div class="ame-pwc-cfg__no-match">' +
          '<p>No exact matches found for your criteria.</p>' +
          '<p>Try adjusting your preferences or <a href="tel:+18039261493">call (803) 926-1493</a> for expert help.</p>' +
        '</div>';
      resultsSubtitle.textContent = '';
      announce('No exact matches found. Try adjusting your preferences or call for help.');
      return top;
    }

    if (top[0].total <= 0) {
      resultsSubtitle.textContent =
        'We couldn\'t find an exact match, but here are our closest options. ' +
        'Call (803) 926-1493 for personalized help.';
    } else {
      resultsSubtitle.textContent = 'Based on your answers, here are our top ' + top.length + ' recommendations.';
    }

    for (var j = 0; j < top.length; j++) {
      var p = top[j].product;
      var specs = buildSpecs(p);
      var specsHtml = '';
      for (var k = 0; k < specs.length; k++) {
        specsHtml += '<span class="ame-pwc-cfg__card-spec">' + escapeHtml(specs[k]) + '</span>';
      }

      var priceHtml = '';
      if (p.call_for_price) {
        priceHtml = '<span style="color:var(--ame-primary)">Call for Price</span>';
      } else {
        priceHtml = formatPrice(p.price);
        if (p.compare_at_price && p.compare_at_price > p.price) {
          priceHtml += ' <span class="ame-card__compare-price">' + formatPrice(p.compare_at_price) + '</span>';
        }
      }

      var vendorHtml = p.vendor ? '<span class="ame-card__vendor">' + escapeHtml(p.vendor) + '</span>' : '';

      var card =
        '<a href="' + escapeHtml(p.url) + '" class="ame-card">' +
          '<div class="ame-card__image-wrap">' +
            '<span class="ame-pwc-cfg__match-badge">' + getMatchLabel(j) + '</span>' +
            (p.image ? '<img class="ame-card__image" src="' + escapeHtml(p.image) + '" alt="' + escapeHtml(p.title) + '" loading="lazy" width="500" height="500">' : '') +
          '</div>' +
          '<div class="ame-card__body">' +
            vendorHtml +
            '<h3 class="ame-card__title">' + escapeHtml(p.title) + '</h3>' +
            '<div class="ame-pwc-cfg__card-specs">' + specsHtml + '</div>' +
            '<div class="ame-card__price">' + priceHtml + '</div>' +
          '</div>' +
        '</a>';

      resultsGrid.insertAdjacentHTML('beforeend', card);
    }

    announce('Found ' + top.length + ' matching power wheelchairs. Showing your top recommendations.');
    return top;
  }

  // --- Build filter URL for "View All" ---
  function buildFilterUrl(ans) {
    var base = '/collections/power-wheelchairs';
    var params = [];

    if (ans.pwc_use_case) {
      switch (ans.pwc_use_case) {
        case 'travel':
          params.push('filter.p.tag=folding');
          break;
        case 'heavy_duty':
          params.push('filter.p.tag=heavy-duty');
          break;
        case 'compact':
          params.push('filter.p.tag=compact');
          break;
      }
    }

    if (ans.pwc_budget) {
      switch (ans.pwc_budget) {
        case '2000':
          params.push('filter.v.price.lte=2000');
          break;
        case '4000':
          params.push('filter.v.price.gte=2000');
          params.push('filter.v.price.lte=4000');
          break;
        case '6000':
          params.push('filter.v.price.gte=4000');
          params.push('filter.v.price.lte=6000');
          break;
        case '9999':
          params.push('filter.v.price.gte=6000');
          break;
      }
    }

    return params.length > 0 ? base + '?' + params.join('&') : base;
  }

  // --- Show results ---
  function showResults() {
    wizard.hidden = true;
    var scored = scoreProducts(answers);
    var top = renderResults(scored);

    if (viewAllLink) viewAllLink.href = buildFilterUrl(answers);

    resultsPanel.classList.add('is-active');
    if (resultsHeading) resultsHeading.focus();

    clearState();
  }

  // --- Event: CTA click ---
  ctaBtn.addEventListener('click', function () {
    if (wizard.hidden && !resultsPanel.classList.contains('is-active')) {
      openWizard();
    } else {
      closeWizard();
    }
  });

  // --- Event: radio change ---
  root.addEventListener('change', function (e) {
    if (e.target.type === 'radio' && e.target.closest('[data-pwc-cfg-step]')) {
      var stepEl = e.target.closest('[data-pwc-cfg-step]');
      updateNextBtn(stepEl);
    }
  });

  // --- Event: next ---
  root.addEventListener('click', function (e) {
    var nextBtn = e.target.closest('[data-pwc-cfg-next]');
    if (!nextBtn || nextBtn.disabled) return;

    collectAnswer();

    if (currentStep < TOTAL_STEPS) {
      showStep(currentStep + 1);
    } else {
      showResults();
    }
  });

  // --- Event: back ---
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-pwc-cfg-back]')) {
      collectAnswer();
      if (currentStep > 1) {
        showStep(currentStep - 1);
      }
    }
  });

  // --- Event: close ---
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-pwc-cfg-close]')) {
      closeWizard();
    }
  });

  // --- Event: restart ---
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-pwc-cfg-restart]')) {
      resultsPanel.classList.remove('is-active');
      answers = {};
      currentStep = 1;

      var radios = root.querySelectorAll('input[type="radio"]');
      for (var i = 0; i < radios.length; i++) {
        radios[i].checked = false;
      }

      clearState();
      openWizard();
    }
  });

  // --- Event: Banner CTA ---
  var bannerCta = document.querySelector('[data-cfg-banner-cta]');
  if (bannerCta) {
    bannerCta.addEventListener('click', function () {
      openWizard();
    });
  }

  // --- Event: Escape key ---
  document.addEventListener('keydown', function (e) {
    if ((e.key === 'Escape' || e.key === 'Esc') && !wizard.hidden) {
      closeWizard();
    }
  });

  // --- Restore session ---
  var saved = loadState();
  if (saved && saved.step && saved.answers) {
    answers = saved.answers;
    currentStep = saved.step;

    var fieldNames = Object.keys(answers);
    for (var f = 0; f < fieldNames.length; f++) {
      var radio = root.querySelector('input[name="' + fieldNames[f] + '"][value="' + answers[fieldNames[f]] + '"]');
      if (radio) radio.checked = true;
    }

    openWizard();
  }
})();
